<template>
  <div>
    <!-- Campo editable en listado (campos planos; group_subfield date se maneja abajo) -->
    <ListEditField
      v-if="canInlineListEdit && field.type !== 'group_subfield' && field.type !== 'reference_subfield'"
      :field="field"
      :value="value"
      :item="item"
      :lists="lists"
      @field-updated="$emit('field-updated', $event)"
    />
    <!-- group isArray + date/text/number/list con listedit: una edición por fila del group -->
    <div
      v-else-if="isGroupScalarListEdit"
      class="group-subfield-array-values"
    >
      <div
        v-for="(row, index) in groupScalarListEditRows"
        :key="`group-scalar-listedit-${row.groupItemId || index}`"
        class="group-subfield-value"
        :style="{ marginBottom: index < groupScalarListEditRows.length - 1 ? '4px' : '0', display: 'block', width: '100%' }"
      >
        <ListEditField
          v-if="row.groupItemId"
          :field="groupScalarListEditField"
          :value="row.value"
          :item="item"
          :lists="lists"
          @field-updated="onGroupScalarListEditUpdated(row, $event)"
        />
        <span v-else-if="emptyMultiLineSlot(row.value)" class="text-muted">-</span>
        <template v-else>
          <component
            v-if="fieldComponent"
            :is="fieldComponent"
            :value="row.value"
            :field="effectiveField"
            :lists="lists"
            :isGroupModel="false"
            :modelName="modelName"
            :item="item"
            :mode="'view'"
            :showLabel="false"
          />
          <span v-else>{{ row.value }}</span>
        </template>
      </div>
      <span
        v-if="groupScalarListEditRows.length === 0"
        class="text-muted"
      >-</span>
    </div>
    <!-- Derived / reference → date/text/number/list con listedit (ej. expenses_courrier.date) -->
    <ListEditField
      v-else-if="isReferenceScalarListEdit && referenceScalarGroupItemId"
      :field="referenceScalarListEditField"
      :value="referenceScalarListEditValue"
      :item="item"
      :lists="lists"
      @field-updated="onReferenceScalarListEditUpdated"
    />
    <!-- Campo status de shipping: colores (number o textlist) -->
    <template v-else-if="modelName === 'shipping' && field.key === 'status'">
      <ShippingStatusBadge
        v-if="item"
        :item="item"
        :model-name="modelName"
        size="small"
      />
      <span
        v-else-if="value != null && value !== ''"
        class="shipping-status-badge"
        :class="getStatusClass(Number(value))"
      >
        {{ getStatusLabel(Number(value), $t) }}
      </span>
      <span v-else class="text-muted">-</span>
    </template>
    <!-- Motivo de balance != 0: badge Info + tooltip -->
    <template v-else-if="modelName === 'shipping' && field.key === 'balance_reason'">
      <BalanceReasonInfoBadge :value="value" />
    </template>
    <!-- Certificados país (BSC/BESC/CNCA/ANOR): un badge por tipo, tooltip = contacto -->
    <template v-else-if="modelName === 'shipping' && field.key === 'certificate_type'">
      <CertificateTypeBadges :value="value" :item="item" />
    </template>
    <!-- Mostrar valores de reference_subfield array uno debajo del otro cuando shouldSum es false o undefined -->
    <div
      v-else-if="field.type === 'reference_subfield' && field.isArray && field.shouldSum !== true && Array.isArray(value) && value.length > 0"
      class="group-subfield-array-values"
    >
      <div
        v-for="(val, index) in value"
        :key="`reference-subfield-${index}`"
        class="group-subfield-value"
        :style="{ marginBottom: index < value.length - 1 ? '4px' : '0', display: 'block', width: '100%' }"
      >
        <span v-if="emptyMultiLineSlot(val)" class="text-muted">-</span>
        <template v-else>
          <component
            v-if="fieldComponent"
            :is="fieldComponent"
            v-bind="compactTextareaBind"
            :value="val"
            :field="effectiveField"
            :referenceOptions="getReferenceOptions(field)"
            :lists="lists"
            :isGroupModel="false"
            :modelName="modelName"
            :item="item"
            :mode="'view'"
            :showLabel="false"
            :hideAddButton="hideReferenceAddButton"
            @open-reference-modal="$emit('go-to-reference', $event)"
            @go-to-reference="$emit('go-to-reference', $event)"
            @go-to-blnumber="$emit('go-to-blnumber', $event)"
          />
          <span v-else>{{ val }}</span>
        </template>
      </div>
    </div>
    <span v-else-if="field.type === 'reference_subfield' && field.isArray && field.shouldSum !== true && (!Array.isArray(value) || value.length === 0)" class="text-muted">-</span>
    <!-- Mostrar valores de group_subfield array uno debajo del otro cuando shouldSum es false o undefined -->
    <div
      v-else-if="field.type === 'group_subfield' && (field.isArray || Array.isArray(value)) && field.shouldSum !== true && Array.isArray(value) && value.length > 0"
      class="group-subfield-array-values"
    >
      <div
        v-for="(val, index) in value"
        :key="`group-subfield-${index}`"
        class="group-subfield-value"
        :style="{ marginBottom: index < value.length - 1 ? '4px' : '0', display: 'block', width: '100%' }"
      >
        <span v-if="emptyMultiLineSlot(val)" class="text-muted">-</span>
        <template v-else>
          <component
            v-if="fieldComponent"
            :is="fieldComponent"
            v-bind="compactTextareaBind"
            :value="val"
            :field="effectiveField"
            :referenceOptions="getReferenceOptions(field)"
            :lists="lists"
            :isGroupModel="false"
            :modelName="modelName"
            :item="item"
            :mode="'view'"
            :showLabel="false"
            :hideAddButton="hideReferenceAddButton"
            @open-reference-modal="$emit('go-to-reference', $event)"
            @go-to-reference="$emit('go-to-reference', $event)"
            @go-to-blnumber="$emit('go-to-blnumber', $event)"
          />
          <span v-else>{{ val }}</span>
        </template>
      </div>
    </div>
    <span v-else-if="field.type === 'group_subfield' && (field.isArray || Array.isArray(value)) && field.shouldSum !== true && (!Array.isArray(value) || value.length === 0)" class="text-muted">-</span>
    <!-- Mostrar valores de campos normales array (text, string) uno debajo del otro cuando shouldSum es false o undefined -->
    <div
      v-else-if="(field.isArray || Array.isArray(value)) && field.shouldSum !== true && Array.isArray(value) && value.length > 0 && (field.type === 'text' || field.type === 'string')"
      class="group-subfield-array-values"
    >
      <div
        v-for="(val, index) in value"
        :key="`text-array-field-${index}`"
        class="group-subfield-value"
        :style="{ marginBottom: index < value.length - 1 ? '4px' : '0', display: 'block', width: '100%' }"
      >
        <span v-if="emptyMultiLineSlot(val)" class="text-muted">-</span>
        <template v-else>
          <component
            v-if="fieldComponent"
            :is="fieldComponent"
            v-bind="compactTextareaBind"
            :value="val"
            :field="effectiveField"
            :referenceOptions="getReferenceOptions(field)"
            :lists="lists"
            :isGroupModel="false"
            :modelName="modelName"
            :item="item"
            :mode="'view'"
            :showLabel="false"
            :hideAddButton="hideReferenceAddButton"
            @open-reference-modal="$emit('go-to-reference', $event)"
            @go-to-reference="$emit('go-to-reference', $event)"
            @go-to-blnumber="$emit('go-to-blnumber', $event)"
          />
          <span v-else>{{ val }}</span>
        </template>
      </div>
    </div>
    <span v-else-if="(field.isArray || Array.isArray(value)) && field.shouldSum !== true && (!Array.isArray(value) || value.length === 0) && (field.type === 'text' || field.type === 'string')" class="text-muted">-</span>
    <!-- Mostrar valores de campos normales array uno debajo del otro cuando shouldSum es false o undefined -->
    <div
      v-else-if="field.isArray && field.shouldSum !== true && Array.isArray(value) && value.length > 0 && (field.type === 'number' || field.type === 'currency')"
      class="group-subfield-array-values"
    >
      <div
        v-for="(val, index) in value"
        :key="`array-field-${index}`"
        class="group-subfield-value"
        :style="{ marginBottom: index < value.length - 1 ? '4px' : '0', display: 'block', width: '100%' }"
      >
        <span v-if="emptyMultiLineSlot(val)" class="text-muted">-</span>
        <template v-else>
          <component
            v-if="fieldComponent"
            :is="fieldComponent"
            v-bind="compactTextareaBind"
            :value="val"
            :field="effectiveField"
            :referenceOptions="getReferenceOptions(field)"
            :lists="lists"
            :isGroupModel="false"
            :modelName="modelName"
            :item="item"
            :mode="'view'"
            :showLabel="false"
            :hideAddButton="hideReferenceAddButton"
            @open-reference-modal="$emit('go-to-reference', $event)"
            @go-to-reference="$emit('go-to-reference', $event)"
            @go-to-blnumber="$emit('go-to-blnumber', $event)"
          />
          <span v-else>{{ val }}</span>
        </template>
      </div>
    </div>
    <span v-else-if="field.isArray && field.shouldSum !== true && (!Array.isArray(value) || value.length === 0) && (field.type === 'number' || field.type === 'currency')" class="text-muted">-</span>
    <!-- list isArray (incl. reference_subfield con subFieldType list): badge + FieldList por ítem -->
    <div
      v-else-if="effectiveFieldType === 'list' && field.isArray && getListArrayValues(value).length > 0"
      class="group-subfield-array-values"
    >
      <span
        v-for="(listItem, index) in getListArrayValues(value)"
        :key="`list-array-${index}`"
        class="badge badge-primary mr-1 d-inline-block"
        :style="listValueBadgeStyle(listItem)"
      >
        <span v-if="emptyMultiLineSlot(listItem)" class="text-muted">-</span>
        <component
          v-else
          :is="fieldComponent"
          v-bind="compactTextareaBind"
          :value="listItem"
          :field="effectiveField"
          :referenceOptions="getReferenceOptions(field)"
          :lists="lists"
          :isGroupModel="false"
          :modelName="modelName"
          :item="item"
          :mode="'view'"
          :showLabel="false"
          @open-reference-modal="$emit('go-to-reference', $event)"
          @go-to-reference="$emit('go-to-reference', $event)"
          @go-to-blnumber="$emit('go-to-blnumber', $event)"
        />
      </span>
    </div>
    <span v-else-if="effectiveFieldType === 'list' && field.isArray" class="text-muted">-</span>
    <!-- list valor único (incl. reference_subfield list): mismo aspecto que en detail (badge) -->
    <span
      v-else-if="effectiveFieldType === 'list' && value != null && value !== '' && !Array.isArray(value)"
      class="badge badge-primary d-inline-block"
      :style="listValueBadgeStyle(value)"
    >
      <component
        :is="fieldComponent"
        v-bind="compactTextareaBind"
        :value="value"
        :field="effectiveField"
        :referenceOptions="getReferenceOptions(field)"
        :lists="lists"
        :isGroupModel="false"
        :modelName="modelName"
        :item="item"
        :mode="'view'"
        :showLabel="false"
        @open-reference-modal="$emit('go-to-reference', $event)"
        @go-to-reference="$emit('go-to-reference', $event)"
        @go-to-blnumber="$emit('go-to-blnumber', $event)"
      />
    </span>
    <!-- Fallback: cualquier array que llegue aquí (p. ej. group_subfield packinglist.container) se muestra item por item; si es list, con badge -->
    <div
      v-else-if="Array.isArray(value) && value.length > 0 && field.shouldSum !== true && fieldComponent"
      class="group-subfield-array-values"
    >
      <span
        v-for="(val, index) in value"
        :key="`array-fallback-${index}`"
        :class="effectiveFieldType === 'list' ? 'badge badge-primary mr-1 d-inline-block' : 'group-subfield-value d-inline-block mr-1'"
        :style="effectiveFieldType === 'list' ? listValueBadgeStyle(val) : null"
      >
        <span v-if="emptyMultiLineSlot(val)" class="text-muted">-</span>
        <component
          v-else
          :is="fieldComponent"
          v-bind="compactTextareaBind"
          :value="val"
          :field="effectiveField"
          :referenceOptions="getReferenceOptions(field)"
          :lists="lists"
          :isGroupModel="false"
          :modelName="modelName"
          :item="item"
          :mode="'view'"
          :showLabel="false"
          @open-reference-modal="$emit('go-to-reference', $event)"
          @go-to-reference="$emit('go-to-reference', $event)"
          @go-to-blnumber="$emit('go-to-blnumber', $event)"
        />
      </span>
    </div>
    <span v-else-if="Array.isArray(value) && value.length === 0" class="text-muted">-</span>
    <span
      v-else-if="
        (field.type === 'group_subfield' || field.type === 'reference_subfield') &&
        emptyMultiLineSlot(value)
      "
      class="text-muted"
      >-</span
    >
    <!-- Componente dinámico con props específicas para cada tipo -->
    <component
      v-else-if="fieldComponent"
      :is="fieldComponent"
      v-bind="compactTextareaBind"
      :value="value"
      :field="effectiveField"
      :referenceOptions="getReferenceOptions(field)"
      :lists="lists"
      :isGroupModel="false"
      :modelName="modelName"
      :item="item"
      :mode="'view'"
      :showLabel="false"
      :hideAddButton="hideReferenceAddButton"
      @open-reference-modal="$emit('go-to-reference', $event)"
      @go-to-reference="$emit('go-to-reference', $event)"
      @go-to-blnumber="$emit('go-to-blnumber', $event)"
    />
    <span v-else-if="field.isArray">
      <span v-for="item in value" :key="item._id" class="badge bg-primary">
        {{ item }}
      </span>
    </span>
    <span
      v-else-if="
        field.type === 'string' &&
        (field.key === 'name' || field.key === 'title') &&
        (item._modelName || modelName) &&
        item._id
      "
      class="d-inline-flex align-items-center gap-1"
    >
      <span
        class="cursor-pointer text-primary text-decoration-underline"
        @click.stop="goToItem(item._id, item._modelName || modelName, item.name || item.title)"
        style="cursor: pointer;"
        >{{ value }}</span
      >
      <a
        :href="getItemUrl(item._id, item._modelName || modelName, item.name || item.title)"
        target="_blank"
        rel="noopener noreferrer"
        @click.stop
        class="text-muted text-decoration-none"
        :title="$t('Open in new tab')"
        style="font-size: 0.8em;"
      >
        <i class="fas fa-external-link-alt"></i>
      </a>
      <MissingPrepaymentWarningIcon
        v-if="showMissingPrepaymentNameWarning"
        :record="item"
      />
    </span>
    <span v-else>{{ value }} </span>
  </div>
</template>

<script>
import { 
  FieldText, 
  FieldDate, 
  FieldNumber, 
  FieldTextList,
  FieldReference,
  FieldList,
  FieldBoolean,
  FieldFile,
  FieldSelect,
  FieldIcon
} from "../model-definition-core";
import ListEditField from "@/components/gotrading/common/ListEditField.vue";
import MissingPrepaymentWarningIcon from "@/components/gotrading/common/MissingPrepaymentWarningIcon.vue";
import FieldGroup from "./FieldGroup.vue";
import ShippingStatusBadge from "@/components/gotrading/common/ShippingStatusBadge.vue";
import BalanceReasonInfoBadge from "@/components/gotrading/common/BalanceReasonInfoBadge.vue";
import CertificateTypeBadges from "@/components/gotrading/common/CertificateTypeBadges.vue";
import { getStatusClass, getStatusLabel } from "@/utils/shippingStatus";
import { buildDynamicModelItemViewPath } from "@/utils/dynamicModelPaths";
import {
  hasMissingAssociatedPrepayment,
  isProformOrShippingModelName,
} from "@/utils/missingPrepaymentWarning";
import { canCurrentUserEditField } from "@/utils/fieldEditableByUser";
import { getFieldListItems } from "@/utils/fieldListItems";
import { getListValueBadgeStyle } from "@/utils/listItemColor";

export default {
  name: "FieldRenderer",
  components: {
    ShippingStatusBadge,
    BalanceReasonInfoBadge,
    CertificateTypeBadges,
    MissingPrepaymentWarningIcon,
    FieldReference,
    FieldDate,
    FieldNumber,
    FieldTextList,
    FieldList,
    FieldBoolean,
    FieldFile,
    FieldSelect,
    FieldIcon,
    FieldGroup,
    FieldText,
    ListEditField,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: false,
    },
    value: {
      type: [String, Number, Object, Array, Date, Boolean],
    },
    referenceItems: {
      type: [Array, Object],
      default: () => ({}),
    },
    lists: {
      type: Array,
      default: () => [],
    },
    modelName: {
      type: String,
      required: false,
    },
    /** En listado (p. ej. dynamic-model): no mostrar botón add en references */
    hideReferenceAddButton: {
      type: Boolean,
      default: false,
    },
    /** Vista tabla: acorta textarea para no inflar filas */
    compactTextarea: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    compactTextareaBind() {
      if (!this.compactTextarea || this.effectiveFieldType !== "textarea") {
        return {};
      }
      return { compactTextarea: true };
    },
    showMissingPrepaymentNameWarning() {
      const model = this.item && (this.item._modelName || this.modelName);
      if (!isProformOrShippingModelName(model)) return false;
      return hasMissingAssociatedPrepayment(this.item);
    },
    canInlineListEdit() {
      return (
        this.field.listedit === true &&
        canCurrentUserEditField(this.field, this.item)
      );
    },
    /** Tipos escalares soportados por ListEditField en group/reference derivados. */
    isListEditScalarType() {
      return [
        "date",
        "datetime",
        "text",
        "string",
        "email",
        "textarea",
        "number",
        "list",
        "file",
      ].includes(this.field.subFieldType);
    },
    /** List edit en group isArray (un nivel, sin group anidado). */
    isGroupScalarListEdit() {
      return (
        this.field.type === "group_subfield" &&
        this.field.listedit === true &&
        this.field.isArray === true &&
        !this.field.parentSubField &&
        this.isListEditScalarType &&
        canCurrentUserEditField(this.field.subFieldDef || this.field, this.item)
      );
    },
    groupScalarListEditField() {
      const sub = this.field.subFieldDef || {};
      return {
        ...sub,
        key: this.field.subFieldKey || sub.key,
        type: this.field.subFieldType || sub.type || "text",
        listedit: true,
        readonly: sub.readonly === true,
      };
    },
    groupScalarListEditRows() {
      const parentKey = this.field.parentField && this.field.parentField.key;
      const subKey = this.field.subFieldKey;
      if (!parentKey || !subKey || !this.item) return [];
      const raw = this.item[parentKey];
      if (!Array.isArray(raw) || raw.length === 0) return [];
      return raw.map((gi) => {
        if (!gi || typeof gi !== "object") {
          return { groupItemId: null, value: null };
        }
        const val = gi[subKey];
        return {
          groupItemId: gi._id || null,
          value: val !== undefined ? val : null,
        };
      });
    },
    /**
     * List edit sobre reference derivado (1 fila → 1 doc del group model).
     * Con una referencia derivada multivalor solo aplica si enlaza una única fila:
     * `referenceScalarGroupItemId` devuelve null con varias y la celda queda de solo
     * lectura, porque no hay forma de saber cuál de ellas se está editando.
     */
    isReferenceScalarListEdit() {
      return (
        this.field.type === "reference_subfield" &&
        this.field.listedit === true &&
        !this.field.nestedFieldKey &&
        (this.field.isArray !== true || this.field.refIsArray === true) &&
        this.isListEditScalarType &&
        !!(this.field.parentField && this.field.parentField.ref) &&
        canCurrentUserEditField(this.field.subFieldDef || this.field, this.item)
      );
    },
    referenceScalarListEditField() {
      const sub = this.field.subFieldDef || {};
      return {
        ...sub,
        key: this.field.subFieldKey || sub.key,
        type: this.field.subFieldType || sub.type || "text",
        listedit: true,
        readonly: sub.readonly === true,
      };
    },
    /**
     * Valor suelto para la edición inline.
     *
     * En un campo derivado multivalor el valor de la celda llega como array (una posición
     * por fila enlazada, ver getFieldValue). Acá siempre hay una sola fila —
     * `referenceScalarGroupItemId` es null con varias — pero ListEditField espera el valor
     * escalar: con el array, la vista queda en blanco y solo se ve al abrir el editor,
     * porque el input castea `[1500]` a "1500".
     */
    referenceScalarListEditValue() {
      if (!Array.isArray(this.value)) return this.value;
      return this.value.length === 1 ? this.value[0] : null;
    },
    referenceScalarGroupItemId() {
      const parent = this.field.parentField;
      if (!parent || !parent.key || !this.item) return null;
      const parentVal = this.item[parent.key];
      // Referencia derivada multivalor: editable solo cuando enlaza una única fila.
      if (Array.isArray(parentVal)) {
        if (parentVal.length !== 1) return null;
        const only = parentVal[0];
        if (only && typeof only === "object") return only._id || null;
        return only != null && only !== "" ? only : null;
      }
      if (parentVal && typeof parentVal === "object") {
        return parentVal._id || null;
      }
      if (parentVal != null && parentVal !== "") return parentVal;
      return null;
    },
    // Para reference_subfield y group_subfield, usar el tipo real del subcampo
    effectiveFieldType() {
      if (this.field.type === 'reference_subfield' && this.field.subFieldType) {
        return this.field.subFieldType;
      }
      if (this.field.type === 'group_subfield' && this.field.subFieldType) {
        return this.field.subFieldType;
      }
      return this.field.type;
    },
    // Campo efectivo que normaliza las propiedades para subcampos de reference/group
    effectiveField() {
      if (this.field.type === 'reference_subfield' || this.field.type === 'group_subfield') {
        // Obtener la definición completa del subcampo si existe
        const subFieldDef = this.field.subFieldDef || {};
        
        return {
          // Primero las propiedades del subcampo original (incluye formato, currency, decimals, etc.)
          ...subFieldDef,
          // Luego las propiedades del campo reference_subfield
          ...this.field,
          // Finalmente sobreescribir con el tipo efectivo y propiedades específicas
          type: this.effectiveFieldType,
          listType: this.field.subFieldListType || subFieldDef.listType || this.field.listType,
          listRef: this.field.subFieldListRef || subFieldDef.listRef || this.field.listRef,
          ref: this.field.subFieldRef || subFieldDef.ref || this.field.ref,
          // Propiedades de formato del subcampo
          currencySymbol: this.field.subFieldCurrencySymbol || subFieldDef.currencySymbol,
          decimals: this.field.subFieldDecimals ?? subFieldDef.decimals,
          format: this.field.subFieldFormat || subFieldDef.format,
          prefix: this.field.subFieldPrefix || subFieldDef.prefix,
          suffix: this.field.subFieldSuffix || subFieldDef.suffix,
          parent: this.field.parent ? this.field.parent : null
        };
      }
      return this.field;
    },
    fieldComponent() {
      const fieldType = this.effectiveFieldType;
      switch (fieldType) {
        case "reference":
        case "users":
          return "FieldReference";
        case "string":
        case "text":
        case "email":
        case "textarea":
        case "varchar":
        case "char":
        case "autoincrement":
          return "FieldText";
        case "date":
        case "datetime":
          return "FieldDate";
        case "list":
          return "FieldList";
        case "group":
          return "FieldGroup";
        case "textlist":
          return "FieldTextList";
        case "boolean":
          return "FieldBoolean";
        case "file":
        case "image":
          return "FieldFile";
        case "select":
          return "FieldSelect";
        case "icon":
          return "FieldIcon";
        case "number":
        case "currency":
          return "FieldNumber";
        default:
          return null;
      }
    },
  },
  methods: {
    getStatusClass,
    getStatusLabel,
    /**
     * Fondo del badge de un valor de lista con el color que el admin le puso al ítem en
     * el editor de listas. `null` (badge por defecto) si ese ítem no tiene color.
     */
    listValueBadgeStyle(val) {
      return getListValueBadgeStyle(
        val,
        getFieldListItems(this.effectiveField, {
          lists: this.lists,
          referenceItems: this.referenceItems,
        })
      );
    },
    onGroupScalarListEditUpdated(row, event) {
      const parentKey =
        this.field.parentField && this.field.parentField.key;
      if (!row || !row.groupItemId || !parentKey || !this.modelName) return;
      this.$emit("field-updated", {
        fieldKey: this.field.subFieldKey,
        itemId: this.item && this.item._id,
        value: event.value,
        previousValue: row.value,
        groupItemId: row.groupItemId,
        groupModelName: `${this.modelName}_${parentKey}`,
        parentGroupKey: parentKey,
      });
    },
    onReferenceScalarListEditUpdated(event) {
      const parent = this.field.parentField;
      const groupItemId = this.referenceScalarGroupItemId;
      if (!parent || !parent.ref || !groupItemId) return;
      this.$emit("field-updated", {
        fieldKey: this.field.subFieldKey,
        itemId: this.item && this.item._id,
        value: event.value,
        previousValue: this.value,
        groupItemId,
        groupModelName: parent.ref,
        parentGroupKey: parent.key,
      });
    },
    /** Filas multi-línea (grupo / subcampo derivado): hueco sin dato → guión como en Excel */
    emptyMultiLineSlot(val) {
      return val === null || val === undefined || val === "";
    },
    /** Para list isArray: nunca pasar array completo a un solo FieldList. Si value es string "a, b", normalizar a [a, b]. */
    getListArrayValues(raw) {
      if (raw == null || raw === "") return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        return raw.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
      }
      return [raw];
    },
    getRefModel(field) {
      // Para reference_subfield, usar subFieldRef
      if (field.type === 'reference_subfield' && field.subFieldRef) {
        return field.subFieldRef;
      }
      
      // Para group_subfield, usar la referencia del subcampo si existe
      if (field.type === 'group_subfield' && field.subFieldRef) {
        return field.subFieldRef;
      }
      
      // Si el campo tiene ref definido, usarlo
      if (field.ref) {
        return field.ref;
      }

      // Si no, inferir del nombre del campo
      if (field.key === "shippings") {
        return "shipping";
      }
      if (field.key === "proforms") {
        return "proform";
      }
      if (field.key === "clients") {
        return "client";
      }

      return field.key;
    },
    getReferenceOptions(field) {
      // Solo para campos de tipo reference
      if (field.type !== 'reference' && field.type !== 'reference_subfield' && field.type !== 'users') {
        return [];
      }
      
      const refModel = this.getRefModel(field);
      if (!refModel || !this.referenceItems) {
        return [];
      }
      
      // Buscar en referenceItems con diferentes variaciones del nombre
      const refItems = this.referenceItems[refModel] ||
                      this.referenceItems[refModel.toLowerCase()] ||
                      this.referenceItems[refModel.charAt(0).toUpperCase() + refModel.slice(1)] ||
                      [];
      
      // Asegurar que sea un array
      return Array.isArray(refItems) ? refItems : [];
    },
    goToItem(itemId, modelName, title) {
      if (!itemId || !modelName) {
        console.log('[FieldRenderer.goToItem] ⚠️ Faltan parámetros:', { itemId, modelName });
        return;
      }
      const url = buildDynamicModelItemViewPath(modelName, itemId, title);
      this.$router.push(url).catch(err => {
        // Si hay error de navegación duplicada, ignorarlo
        if (err.name !== 'NavigationDuplicated') {
          console.error('Error navigating to item:', err);
        }
      });
    },
    getItemUrl(itemId, modelName, title) {
      if (!itemId || !modelName) return '#';
      return buildDynamicModelItemViewPath(modelName, itemId, title);
    },
  },
};
</script>

<style scoped>
/* Estilos específicos para FieldRenderer */
.group-subfield-array-values {
  display: flex;
  flex-direction: column;
}

.group-subfield-value {
  display: block;
  width: 100%;
}
</style>
